import { useEffect, useState } from "react";
import { useAuthContext } from "@/context/AuthContext";
import {
  getWorkspaceValueChainImportance,
  listAssets,
  listValueChainStages,
  type Asset,
  type ValueChainImportanceRating,
  type ValueChainStage,
} from "@/lib/assets";
import { getResults, type AssetHazardResult } from "@/lib/results";
import {
  assetsByCountry,
  assetsByStage,
  computePortfolioStats,
  computeReadinessStats,
} from "@/lib/dashboard-stats";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { ReadinessPanel } from "@/components/dashboard/ReadinessPanel";
import { RiskHorizonColumns } from "@/components/dashboard/RiskHorizonColumns";
import { PortfolioCharts } from "@/components/dashboard/PortfolioCharts";
import { DashboardOnboarding } from "@/components/dashboard/DashboardOnboarding";
import type { ClimateScenario } from "@shared/scoring/types";

// Third copy of this two-entry map (ValueChainImportanceForm.tsx and
// AdminStats.tsx have their own). Deliberately not extracted: a shared
// constant for two words is not worth a new module, and hoisting it is
// orthogonal to this feature. If a fourth copy appears, extract it then.
const SECTOR_LABELS: Record<string, string> = { energy: "Energy", agriculture: "Agriculture" };

function Dashboard() {
  const { workspace } = useAuthContext();

  // Fast group: three plain table reads. The page renders as soon as these land.
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [stages, setStages] = useState<ValueChainStage[]>([]);
  const [ratings, setRatings] = useState<ValueChainImportanceRating[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Slow group: /api/results does one uncached spatial grid lookup per asset,
  // so it loads independently and its failure must not blank the page — §1,
  // §2's first three rows and §4 are all valid without it.
  const [results, setResults] = useState<AssetHazardResult[] | null>(null);
  const [resultsError, setResultsError] = useState<string | null>(null);

  const [scenario, setScenario] = useState<ClimateScenario>("SSP2-4.5");

  useEffect(() => {
    Promise.all([listAssets(), listValueChainStages(), getWorkspaceValueChainImportance()])
      .then(([assetsRes, stagesRes, importanceRes]) => {
        setAssets(assetsRes.assets);
        setStages(stagesRes.stages);
        setRatings(importanceRes.ratings);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard"));

    getResults()
      .then((res) => setResults(res.results))
      .catch((err) => setResultsError(err instanceof Error ? err.message : "Failed to load results"));
  }, []);

  if (error) return <p className="text-sm font-medium text-destructive">{error}</p>;
  if (!assets) return <p className="text-sm text-muted-foreground">Loading...</p>;

  // computeTakeaways needs a record; the API returns an array. Same inline
  // reduction AssessmentResults.tsx does.
  const stageImportance: Record<string, number> = {};
  for (const rating of ratings) stageImportance[rating.valueChainStageId] = rating.importance;

  const header = (
    <div className="flex flex-col gap-0.5">
      {/* text-xl, matching every other page inside the shell. The guidelines
          say text-3xl, which only the auth/legal pages use — see the design
          spec §1 for why that split is an open question rather than something
          this page resolves. */}
      <h1 className="text-xl font-heading font-bold leading-7">Climate risk overview</h1>
      <p className="text-xs leading-4 text-muted-foreground">
        {workspace?.name}
        {workspace?.sector ? ` · ${SECTOR_LABELS[workspace.sector] ?? workspace.sector}` : ""}
      </p>
    </div>
  );

  if (assets.length === 0) {
    return (
      <div className="flex flex-col gap-8">
        {header}
        <DashboardOnboarding />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {header}
      <PortfolioStrip stats={computePortfolioStats(assets, stages)} />
      <ReadinessPanel
        stats={computeReadinessStats(assets, stages, ratings, results)}
        coverageLoading={results === null && resultsError === null}
        coverageError={resultsError}
      />
      <RiskHorizonColumns
        results={results}
        error={resultsError}
        assets={assets}
        stageImportance={stageImportance}
        scenario={scenario}
        onScenarioChange={setScenario}
      />
      <PortfolioCharts byCountry={assetsByCountry(assets)} byStage={assetsByStage(assets, stages)} />
    </div>
  );
}

export default Dashboard;
